import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, ShieldAlert, Bug, Gauge, Store, MapPin, Clock, Check, UtensilsCrossed } from "lucide-react";
import { FilterState, PeriodFilter } from "./CompactFilterBar";
import { RISK_CATEGORIES } from "@/lib/recallUtils";
import { cn } from "@/lib/utils";
import { RETAILER_CATEGORIES } from "@/config/retailerKeywords";
import { CATEGORY_LABELS } from "@/config/categories";
import { MultiSelectDropdown, MultiSelectGroup, MultiSelectOption } from "@/components/ui/multi-select-dropdown";

interface FilterContentProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onApply?: () => void;
  onReset: () => void;
  layout?: 'mobile' | 'desktop';
}

const riskCategoryOptions = [
  { value: 'all', label: 'Toutes catégories' },
  { value: 'pathogen', label: `${RISK_CATEGORIES.pathogen.emoji} ${RISK_CATEGORIES.pathogen.label}` },
  { value: 'chemical', label: `${RISK_CATEGORIES.chemical.emoji} ${RISK_CATEGORIES.chemical.label}` },
  { value: 'allergen', label: `${RISK_CATEGORIES.allergen.emoji} ${RISK_CATEGORIES.allergen.label}` },
  { value: 'packaging', label: `${RISK_CATEGORIES.packaging.emoji} ${RISK_CATEGORIES.packaging.label}` },
  { value: 'other', label: `${RISK_CATEGORIES.other.emoji} ${RISK_CATEGORIES.other.label}` },
];

// Pathogènes triés alphabétiquement
const bacteriaOptions: MultiSelectOption[] = [
  { value: 'bacillus_cereus', label: 'B. cereus' },
  { value: 'clostridium_botulinum', label: 'Botulisme' },
  { value: 'clostridium_perfringens', label: 'C. perfringens' },
  { value: 'campylobacter', label: 'Campylobacter' },
  { value: 'ecoli', label: 'E. coli' },
  { value: 'listeria', label: 'Listeria' },
  { value: 'norovirus', label: 'Norovirus' },
  { value: 'pathogene_autre', label: 'Pathogène autre' },
  { value: 'rotavirus', label: 'Rotavirus' },
  { value: 'staphylococcus', label: 'S. aureus' },
  { value: 'salmonella', label: 'Salmonella' },
  { value: 'toxoplasma', label: 'Toxoplasma' },
];

const severityOptions = [
  { value: 'all', label: 'Toute sévérité' },
  { value: 'high', label: 'Risque élevé' },
  { value: 'medium', label: 'Risque modéré' },
  { value: 'low', label: 'Risque faible' },
];

// Régions groupées - triées alphabétiquement
const regionGroups: MultiSelectGroup[] = [
  {
    id: 'metropole',
    label: '🇫🇷 Métropole',
    options: [
      { value: 'auvergne-rhône-alpes', label: 'Auvergne-Rhône-Alpes' },
      { value: 'bourgogne-franche-comté', label: 'Bourgogne-Franche-Comté' },
      { value: 'bretagne', label: 'Bretagne' },
      { value: 'centre-val de loire', label: 'Centre-Val de Loire' },
      { value: 'corse', label: 'Corse' },
      { value: 'grand est', label: 'Grand Est' },
      { value: 'hauts-de-france', label: 'Hauts-de-France' },
      { value: 'île-de-france', label: 'Île-de-France' },
      { value: 'normandie', label: 'Normandie' },
      { value: 'nouvelle-aquitaine', label: 'Nouvelle-Aquitaine' },
      { value: 'occitanie', label: 'Occitanie' },
      { value: 'pays de la loire', label: 'Pays de la Loire' },
      { value: 'provence-alpes-côte d\'azur', label: 'PACA' },
    ]
  },
  {
    id: 'outremer',
    label: '🌴 Outre-mer',
    options: [
      { value: 'dom-tom', label: 'DOM-TOM (tous)' },
    ]
  }
];

// Enseignes groupées par catégorie
const retailerGroups: MultiSelectGroup[] = RETAILER_CATEGORIES.map(category => ({
  id: category.id,
  label: category.label,
  options: category.retailers.map(retailer => ({
    value: retailer,
    label: retailer
  }))
}));

// Product categories - flat list sorted alphabetically
const productCategoryOptions: MultiSelectOption[] = Object.entries(CATEGORY_LABELS)
  .filter(([id]) => ['18','19','20','21','25','40','45','50','51','64','73','76','79','80','85','94','100'].includes(id))
  .map(([id, label]) => ({ value: id, label }))
  .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

export const FilterContent = ({ 
  filters, 
  onFiltersChange,
  onApply,
  onReset,
  layout = 'mobile'
}: FilterContentProps) => {
  const isDesktop = layout === 'desktop';

  const showPathogens = filters.riskCategory === 'all' || filters.riskCategory === 'pathogen';

  const periodLabels: Record<PeriodFilter, string> = {
    '7d': '7 jours',
    '30d': '30 jours',
    '90d': '90 jours'
  };

  return (
    <div className={cn(
      "space-y-4",
      isDesktop && "py-4"
    )}>
      {/* Période - Mobile only (first item) */}
      {!isDesktop && (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Période
          </label>
          <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
            {(['7d', '30d', '90d'] as PeriodFilter[]).map((period) => (
              <button
                key={period}
                onClick={() => onFiltersChange({ ...filters, period })}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  filters.period === period
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {periodLabels[period]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid layout for all dropdowns */}
      <div className={cn(
        "space-y-3",
        isDesktop && "grid grid-cols-2 lg:grid-cols-3 gap-3 space-y-0"
      )}>
        {/* 1. Catégories de produits */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Catégories
          </label>
          <MultiSelectDropdown
            label="Catégories"
            icon={UtensilsCrossed}
            options={productCategoryOptions}
            selected={filters.categories || []}
            onChange={(categories) => onFiltersChange({ ...filters, categories })}
          />
        </div>

        {/* 2. Enseignes */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Enseignes
          </label>
          <MultiSelectDropdown
            label="Enseignes"
            icon={Store}
            groups={retailerGroups}
            selected={filters.retailers || []}
            onChange={(retailers) => onFiltersChange({ ...filters, retailers })}
            searchable={true}
          />
        </div>

        {/* 2. Régions */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Régions
          </label>
          <MultiSelectDropdown
            label="Régions"
            icon={MapPin}
            groups={regionGroups}
            selected={filters.regions || []}
            onChange={(regions) => onFiltersChange({ ...filters, regions })}
          />
        </div>

        {/* 3. Pathogènes */}
        {showPathogens && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Pathogènes
            </label>
            <MultiSelectDropdown
              label="Pathogènes"
              icon={Bug}
              options={bacteriaOptions}
              selected={filters.agents || []}
              onChange={(agents) => onFiltersChange({ ...filters, agents })}
            />
            {filters.pregnancyOnly && (
              <p className="text-xs text-muted-foreground">
                ℹ️ Agents pré-sélectionnés par le filtre "Grossesse"
              </p>
            )}
          </div>
        )}

        {/* 4. Catégorie de risque */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Catégorie de risque
          </label>
          <Select 
            value={filters.riskCategory || 'all'} 
            onValueChange={(value) => onFiltersChange({ 
              ...filters, 
              riskCategory: value as FilterState['riskCategory'],
              agents: value !== 'all' && value !== 'pathogen' ? [] : filters.agents
            })}
          >
            <SelectTrigger className="h-10">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {riskCategoryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 5. Sévérité */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Sévérité
          </label>
          <Select 
            value={filters.severity} 
            onValueChange={(value) => onFiltersChange({ ...filters, severity: value as FilterState['severity'] })}
          >
            <SelectTrigger className="h-10">
              <div className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {severityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Selected filters summary */}
      {((filters.categories?.length || 0) > 0 ||
        (filters.retailers?.length || 0) > 0 || 
        (filters.regions?.length || 0) > 0 || 
        (filters.agents?.length || 0) > 0) && (
        <div className="flex flex-wrap gap-1.5 pt-2">
          {filters.categories?.map(c => (
            <Badge key={c} variant="secondary" className="text-xs h-6">
              {CATEGORY_LABELS[c] || c}
              <X 
                className="h-3 w-3 ml-1 cursor-pointer hover:text-destructive" 
                onClick={() => onFiltersChange({ 
                  ...filters, 
                  categories: filters.categories?.filter(x => x !== c) 
                })}
              />
            </Badge>
          ))}
          {filters.retailers?.map(r => (
            <Badge key={r} variant="secondary" className="text-xs h-6">
              {r}
              <X 
                className="h-3 w-3 ml-1 cursor-pointer hover:text-destructive" 
                onClick={() => onFiltersChange({ 
                  ...filters, 
                  retailers: filters.retailers?.filter(x => x !== r) 
                })}
              />
            </Badge>
          ))}
          {filters.regions?.map(r => (
            <Badge key={r} variant="secondary" className="text-xs h-6">
              {r === 'dom-tom' ? 'DOM-TOM' : r}
              <X 
                className="h-3 w-3 ml-1 cursor-pointer hover:text-destructive" 
                onClick={() => onFiltersChange({ 
                  ...filters, 
                  regions: filters.regions?.filter(x => x !== r) 
                })}
              />
            </Badge>
          ))}
          {filters.agents?.map(a => {
            const agent = bacteriaOptions.find(b => b.value === a);
            return (
              <Badge key={a} variant="secondary" className="text-xs h-6">
                {agent?.label || a}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer hover:text-destructive" 
                  onClick={() => onFiltersChange({ 
                    ...filters, 
                    agents: filters.agents?.filter(x => x !== a) 
                  })}
                />
              </Badge>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className={cn(
        "flex gap-2 pt-3 border-t",
        isDesktop ? "justify-start" : ""
      )}>
        <Button 
          variant="outline" 
          className={cn(!isDesktop && "flex-1")}
          onClick={onReset}
        >
          <X className="h-4 w-4 mr-2" />
          Réinitialiser
        </Button>
        
        {onApply && (
          <Button 
            className={cn(!isDesktop && "flex-1")}
            onClick={onApply}
          >
            <Check className="h-4 w-4 mr-2" />
            Appliquer
          </Button>
        )}
      </div>
    </div>
  );
};
